import { useQueryClient } from '@tanstack/react-query';
import { useTranslation } from '../../i18n/useTranslation';
import { LiveRegion } from '../atoms/LiveRegion/LiveRegion';
import { Stack } from '../atoms/Stack/Stack';
import { Text } from '../atoms/Typography/Text';
import { LoadErrorState } from '../feedback/LoadErrorState/LoadErrorState';
import { Section } from '../templates/Section/Section';
import { controllerKeys, useControllerSources, useSensorMappings } from '../../hooks/useController';
import { ControllerSources } from './ControllerSources';
import { HomeAssistantSetup } from './HomeAssistantSetup';
import { SensorMappings } from './SensorMappings';
import { EnergyOverview } from './EnergyOverview';
import './Controller.css';

/** German beginner guide (ProfiLux → Home Assistant → ReefGuide), served from public/docs. */
const BASE = import.meta.env.BASE_URL ?? '/';
export const CONTROLLER_GUIDE_URL = `${BASE.endsWith('/') ? BASE : `${BASE}/`}docs/anleitung-ghl-profilux-home-assistant.pdf`;

export interface ControllerPanelProps {
  aquariumId: string | undefined;
}

/**
 * Aquarium controller tab of the integrations page (#1899): sources (device
 * tokens), the generated Home Assistant configuration, the received values and
 * their mapping, and the measured electricity.
 */
export function ControllerPanel({ aquariumId }: ControllerPanelProps) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const sources = useControllerSources(aquariumId);
  const mappings = useSensorMappings(aquariumId);

  let body;
  if (!aquariumId) {
    body = <Text color="muted">{t('controller.noAquarium')}</Text>;
  } else if (sources.isError || mappings.isError) {
    body = (
      <LoadErrorState
        title={t('controller.loadError')}
        onRetry={() => void queryClient.invalidateQueries({ queryKey: controllerKeys.all(aquariumId) })}
        retryLabel={t('controller.common.retry')}
      />
    );
  } else if (sources.isPending || mappings.isPending) {
    body = (
      <LiveRegion busy>
        <Text color="muted">{t('controller.common.loading')}</Text>
      </LiveRegion>
    );
  } else {
    body = (
      <>
        <ControllerSources aquariumId={aquariumId} sources={sources.data} />
        {sources.data.length > 0 && <HomeAssistantSetup sources={sources.data} mappings={mappings.data} />}
        {sources.data.length > 0 && <SensorMappings aquariumId={aquariumId} mappings={mappings.data} />}
        {sources.data.length > 0 && (
          <Section title={t('controller.energy.title')} description={t('controller.energy.description')}>
            <EnergyOverview aquariumId={aquariumId} year={new Date().getFullYear()} editable />
          </Section>
        )}
      </>
    );
  }

  return (
    <Stack gap="8" className="controller-panel">
      <Stack gap="2">
        <Text color="secondary">{t('controller.intro')}</Text>
        <Text variant="body-sm" color="secondary">
          <a className="controller-panel__guide-link" href={CONTROLLER_GUIDE_URL} target="_blank" rel="noopener noreferrer">
            {t('controller.guide.label')}
            {` ${t('controller.guide.opensInNewTab')}`}
          </a>
        </Text>
      </Stack>
      {body}
    </Stack>
  );
}
